import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

const presentDetails = (entries = []) => entries.filter(([label, value]) =>
  value != null && String(value).trim() && value !== '-' && !(label === 'Add-on' && value === 'No'));
const detailLines = entries => presentDetails(entries).map(([label, value]) => `${label}: ${value}`);

// Never collapse differing item statuses or elapsed times into a single misleading result.
export const summarizeItemScreens = (screens = []) => {
  if (!screens.length) return 'No screen data';
  const groups = new Map();
  screens.forEach(screen => {
    const values = Object.fromEntries(screen.details || []);
    const status = String(values.Status || 'Unknown').toUpperCase();
    const elapsed = values['Time to Make'] || '-';
    const key = JSON.stringify([status, elapsed]);
    if (!groups.has(key)) groups.set(key, { status, elapsed, names: [] });
    groups.get(key).names.push(screen.name);
  });
  return [...groups.values()].map(group => {
    const label = groups.size === 1 && screens.length > 1 ? `All ${screens.length} item screens` : group.names.join(', ');
    return `${label}: ${group.status} | ${group.elapsed}`;
  }).join('\n');
};

// Use sans-serif text and aligned screen rows so multi-screen clubs remain easy to scan.
export const createTicketPDF = ({ orderId, details = [], stations = [], courses = [] }) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  doc.setLineHeightFactor(1.05);
  const width = doc.internal.pageSize.getWidth();
  const margin = 12;
  const zone = details.find(([label]) => label === 'Report timezone')?.[1];
  let y = 14;
  const ink = [31, 41, 55];
  const line = [203, 213, 225];
  const text = (value, size, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(...ink);
    return String(value ?? '-');
  };
  doc.text(text('Order Ticket', 14, true), width / 2, y, { align: 'center' });
  y += 4;
  const values = Object.fromEntries(details);
  const header = [
    `Order ID: ${orderId ?? '-'}`,
    `Table: ${values.Table || '-'} | Server: ${values.Server || '-'}`,
    `Member: ${values.Member || '-'}`,
    ...detailLines(details.filter(([label]) => !['Table', 'Server', 'Member', 'Report timezone'].includes(label))),
  ];
  autoTable(doc, {
    startY: y, margin: { top: 12, bottom: 16, left: margin, right: margin },
    body: header.map(value => [value]), theme: 'plain',
    styles: { font: 'helvetica', fontSize: 10, halign: 'center', cellPadding: 0.7, textColor: ink },
  });
  y = doc.lastAutoTable.finalY + 4;
  const ensureSpace = needed => {
    if (y + needed > 261) { doc.addPage(); y = 15; }
  };
  const section = title => {
    ensureSpace(25);
    doc.setDrawColor(...line);
    doc.line(margin, y, width - margin, y);
    y += 4;
    doc.text(text(title, 10, true), margin, y);
    y += 2;
  };
  const card = (title, lines, status = '') => {
    ensureSpace(18);
    const state = String(status).toUpperCase();
    const colors = state === 'BUMPED' ? { fillColor: [220, 252, 231], textColor: [21, 128, 61] }
      : state === 'VOIDED' ? { fillColor: [254, 226, 226], textColor: [185, 28, 28] }
      : state === 'PARKED' ? { fillColor: [254, 243, 199], textColor: [146, 64, 14] }
      : { fillColor: [239, 246, 255], textColor: [30, 64, 175] };
    autoTable(doc, {
      startY: y, margin: { top: 15, bottom: 18, left: margin, right: margin },
      head: [[{ content: title, styles: { cellWidth: width - margin * 2 - 29 } },
        { content: state, styles: { ...(state ? colors : {}), cellWidth: 29, halign: 'center' } }]],
      body: [[{ content: lines.join('\n') || '-', colSpan: 2 }]],
      theme: 'plain', tableLineColor: line, tableLineWidth: 0.2, rowPageBreak: 'avoid',
      styles: { font: 'helvetica', fontSize: 9, textColor: ink, overflow: 'linebreak', cellPadding: 1 },
      headStyles: { fillColor: [248, 250, 252], textColor: ink, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { font: 'helvetica', fontSize: 9, fillColor: [248, 250, 252], textColor: [55, 65, 81] },
      columnStyles: { 1: { cellWidth: 29, halign: 'center' } },
    });
    y = doc.lastAutoTable.finalY + 1;
  };
  // Keep dates intact and put the time on a second line; never convert location-local timestamps here.
  const timestamp = value => String(value || '-').replace(/^(.*\d{4}),\s*/, '$1\n');
  const screenTable = screens => {
    ensureSpace(20);
    const body = screens.map(station => {
      const values = Object.fromEntries(station.details);
      const extra = detailLines(station.details.filter(([label, value]) =>
        !['Status', 'Received', 'Bumped', 'Time to Make'].includes(label) && !(label === 'Timezone' && value === zone)));
      return [station.name + (extra.length ? `\n${extra.join('\n')}` : ''), String(values.Status || '-').toUpperCase(),
        timestamp(values.Received), timestamp(values.Bumped), values['Time to Make'] || '-'];
    });
    autoTable(doc, {
      startY: y, margin: { top: 15, bottom: 18, left: margin, right: margin },
      head: [['Screen', 'Status', 'Received', 'Bumped', 'Elapsed']], body,
      theme: 'plain', rowPageBreak: 'avoid',
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 1.4, textColor: ink, overflow: 'linebreak',
        lineColor: line, lineWidth: { bottom: 0.15 } },
      headStyles: { fillColor: [237, 243, 250], textColor: ink, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 0: { cellWidth: width - margin * 2 - 121 }, 1: { cellWidth: 24 },
        2: { cellWidth: 37 }, 3: { cellWidth: 37 }, 4: { cellWidth: 23, halign: 'right' } },
      didParseCell: cell => {
        if (cell.section !== 'body' || cell.column.index !== 1) return;
        const colors = { BUMPED: [21, 128, 61], VOIDED: [185, 28, 28], PARKED: [146, 64, 14] };
        cell.cell.styles.textColor = colors[String(cell.cell.raw)] || ink;
        cell.cell.styles.fontStyle = 'bold';
      },
    });
    y = doc.lastAutoTable.finalY + 2;
  };
  section('Ticket Status By Station');
  if (!stations.length) card('Stations', ['No station-level ticket events found.']);
  if (stations.length) screenTable(stations);
  section('Items By Course');
  if (!courses.length) card('Items', ['No items found.']);
  courses.forEach(course => {
    ensureSpace(25);
    // Keep orderable item content, but leave IDs, categories and course rollups in the detailed viewer.
    const body = course.items.map(item => {
      // Accept older models too, but present all preparation instructions under one familiar label.
      const instructions = presentDetails(item.details.filter(([label]) => ['Modifier', 'Modifiers', 'Condiments'].includes(label)))
        .map(([, value]) => value);
      const notes = instructions.length ? [`Modifiers: ${instructions.join('; ')}`] : [];
      return [item.quantity, `${item.name}${item.isAddon ? ' [ADD-ON]' : ''}${notes.length ? `\n${notes.join('\n')}` : ''}`,
        summarizeItemScreens(item.stations)];
    });
    autoTable(doc, {
      startY: y, margin: { top: 15, bottom: 18, left: margin, right: margin },
      head: [[{ content: `Course ${course.course}`, colSpan: 3 }], ['Qty', 'Item / instructions', 'Item status / elapsed']],
      body, theme: 'plain', rowPageBreak: 'avoid',
      styles: { font: 'helvetica', fontSize: 9, cellPadding: 1.5, textColor: ink, overflow: 'linebreak',
        lineColor: line, lineWidth: { bottom: 0.15 } },
      headStyles: { fillColor: [237, 243, 250], textColor: ink, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 0: { cellWidth: 12 }, 1: { cellWidth: width - margin * 2 - 78 }, 2: { cellWidth: 66 } },
    });
    y = doc.lastAutoTable.finalY + 2;
  });
  const count = doc.getNumberOfPages();
  for (let page = 1; page <= count; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text(`Order ${String(orderId ?? '-').slice(0, 40)} | ${page}/${count}`, width - margin, 274, { align: 'right' });
  }
  return doc;
};

export const exportTicketToPDF = model => {
  const safeId = String(model.orderId ?? 'details').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 80);
  createTicketPDF(model).save(`Order-Ticket-${safeId}.pdf`);
};
